import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { Select } from '@/components/ui/Select';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/toast';

import { ENTITY_COPY, savedChanges } from '../copy';
import {
  catalogError,
  codeFragmentError,
  frequencyError,
  requiredTextError,
  reviewFrequencyLabel,
} from '../presentation';
import { CODING_SCHEMES } from '../coding';
import type { CodingScheme, DocumentType } from '../types';
import { useSaveCatalogItem } from '../use-catalogs-admin';
import { TemplatePicker, type TemplateRef } from './TemplatePicker';

const COPY = ENTITY_COPY.document_type;

interface Form {
  codeFragment: string;
  name: string;
  days: string;
  /** G3-2: cómo se codifican los documentos del tipo. */
  codingScheme: CodingScheme;
  /** G3-4: plantilla del tipo (solo al editar: el tipo debe existir). */
  template: TemplateRef | null;
}

const SCHEME_OPTIONS = CODING_SCHEMES.map((s) => ({ value: s.value, label: s.label }));

/** Alta o edición de un tipo de documento. El fragmento no se edita: forma parte
 *  de los códigos ya emitidos. Valida antes de enviar con la regla del API y deja
 *  cada rechazo en su campo, conservando lo escrito (§1.5, UX-ERR-01). */
export function DocumentTypeModal({
  open,
  editing,
  onClose,
}: {
  open: boolean;
  /** null = alta. */
  editing: DocumentType | null;
  onClose: () => void;
}): ReactElement | null {
  const formId = useId();
  const schemeId = useId();
  const schemeHintId = useId();
  const save = useSaveCatalogItem('document-types');
  const { showToast } = useToast();
  const [form, setForm] = useState<Form>({
    codeFragment: '',
    name: '',
    days: '365',
    codingScheme: 'proceso',
    template: null,
  });
  const [checked, setChecked] = useState(false);
  const [server, setServer] = useState<ReturnType<typeof catalogError> | null>(null);

  useEffect(() => {
    if (open) {
      setForm({
        codeFragment: editing?.codeFragment ?? '',
        name: editing?.name ?? '',
        days: String(editing?.reviewFrequencyDays ?? 365),
        codingScheme: editing?.codingScheme ?? 'proceso',
        template: editing?.template ?? null,
      });
      setChecked(false);
      setServer(null);
    }
  }, [open, editing]);

  const set = (patch: Partial<Form>): void => {
    setForm((f) => ({ ...f, ...patch }));
    setServer(null);
  };
  const codeMsg =
    server?.field === 'code'
      ? server.message
      : checked && editing === null
        ? codeFragmentError(form.codeFragment)
        : null;
  const nameMsg =
    server?.field === 'name'
      ? server.message
      : checked
        ? requiredTextError(form.name, 200, 'Escribe el nombre del tipo')
        : null;
  const daysMsg = checked ? frequencyError(form.days) : null;
  const daysHint =
    frequencyError(form.days) === null
      ? `Equivale a ${reviewFrequencyLabel(Number(form.days.trim())).toLowerCase()}`
      : 'Cada cuántos días se revisan los documentos de este tipo';

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setChecked(true);
    const invalid =
      (editing === null && codeFragmentError(form.codeFragment) !== null) ||
      requiredTextError(form.name, 200, '') !== null ||
      frequencyError(form.days) !== null;
    if (invalid || save.isPending) {
      return;
    }
    const name = form.name.trim();
    const reviewFrequencyDays = Number(form.days.trim());
    try {
      if (editing === null) {
        const codeFragment = form.codeFragment.trim().toUpperCase();
        await save.mutateAsync({
          id: null,
          body: { codeFragment, name, reviewFrequencyDays, codingScheme: form.codingScheme },
        });
        showToast(COPY.created(name));
      } else {
        // Solo lo que cambió; sin cambios se cierra sin llamar al API.
        const body = {
          ...(name !== editing.name ? { name } : {}),
          ...(reviewFrequencyDays !== editing.reviewFrequencyDays ? { reviewFrequencyDays } : {}),
          ...(form.codingScheme !== editing.codingScheme
            ? { codingScheme: form.codingScheme }
            : {}),
          ...((form.template?.id ?? null) !== editing.templateEntryId
            ? { templateEntryId: form.template?.id ?? null }
            : {}),
        };
        if (Object.keys(body).length > 0) {
          await save.mutateAsync({ id: editing.id, body });
          showToast(savedChanges(name));
        }
      }
      onClose();
    } catch (err) {
      setServer(catalogError(err, 'document_type'));
    }
  }

  return (
    <ModalShell
      open={open}
      title={editing === null ? COPY.newTitle : COPY.editTitle}
      busy={save.isPending}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </button>
          <button type="submit" form={formId} className="btn sm" disabled={save.isPending}>
            {save.isPending
              ? 'Guardando…'
              : editing === null
                ? COPY.createLabel
                : 'Guardar cambios'}
          </button>
        </>
      }
    >
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <TextField
          label="Fragmento de código"
          value={form.codeFragment}
          maxLength={8}
          placeholder="P"
          mono
          disabled={editing !== null}
          hint={
            editing === null
              ? 'Letras y números, hasta 8. Se usa en el código de los documentos.'
              : 'No se cambia: ya forma parte de los códigos emitidos.'
          }
          error={codeMsg}
          onChange={(codeFragment) => set({ codeFragment })}
        />
        <TextField
          label="Nombre"
          value={form.name}
          maxLength={200}
          placeholder="Procedimiento"
          error={nameMsg}
          onChange={(name) => set({ name })}
        />
        <TextField
          label="Revisión cada (días)"
          value={form.days}
          maxLength={4}
          inputMode="numeric"
          hint={daysHint}
          error={daysMsg}
          onChange={(days) => set({ days })}
        />
        <div className="fld">
          <label htmlFor={schemeId} className="fld-lab">
            Codificación
          </label>
          <Select
            id={schemeId}
            value={form.codingScheme}
            options={SCHEME_OPTIONS}
            ariaDescribedBy={schemeHintId}
            onChange={(v) => set({ codingScheme: v as CodingScheme })}
          />
          <span id={schemeHintId} className="fld-hint">
            {CODING_SCHEMES.find((c) => c.value === form.codingScheme)?.hint}
          </span>
        </div>
        {editing !== null && (
          <TemplatePicker value={form.template} onChange={(template) => set({ template })} />
        )}
        {server !== null && server.field === null && (
          <p className="fld-error" role="alert">
            {server.message}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
